<template>
    <div class="game-item" :class="{'viewport-intersected': isMounted}" @mouseover="hover"
         @mouseleave="leave">
        <div class="game-item__box" @click="$el.classList.toggle('active')">
            <div class="game-item__game-restricted" v-if="restrictionReasonTranslationKey">
                <img src="/public/icons/game-restricted-r.svg" alt="Game restricted" @click="showRestrictionMessage"/>
            </div>

            <picture>
                <source :srcset="gameWebpImageSrcset" type="image/webp">
                <img :src="gameJpgImageSrc" :alt="item.title"/>
            </picture>

            <div class="game-item__name" :class="{'game-restricted': restrictionReasonTranslationKey}">
                {{ item.title }}
            </div>
            <div class="label-top">
                <div class="label-new" v-if="item.isNew">{{ $t('front.game.badge.new') }}</div>
                <div class="infGame">{{ item.platformTitle }}</div>
            </div>
            <div class="game-item__hover">
                <v-btn @click="switchFavoriteStatus" v-show="user" :class="{active: isGameFavorite}">
                    <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                                d="M8.36098 2.91864L8.9999 3.95816L9.63888 2.91867C10.3644 1.73841 11.6593 0.953613 13.1391 0.953613C15.4095 0.953613 17.25 2.79406 17.25 5.06441C17.25 6.29893 16.704 7.40389 15.8384 8.15893L15.8192 8.17572L15.8011 8.19379L8.99994 14.9952L2.19879 8.19379L2.18073 8.17573L2.16148 8.15894C1.29597 7.40392 0.75 6.29896 0.75 5.06441C0.75 2.79393 2.59001 0.953613 4.86029 0.953613C6.3402 0.953613 7.63565 1.73851 8.36098 2.91864Z"
                                stroke="white" stroke-width="1.5"/>
                    </svg>
                </v-btn>

                <h4>
                    {{ item.title }}
                    <span>{{ item.platformTitle }}</span>
                </h4>

                <router-link :to="gamePlayRealUrl" class="play" v-show="item.playForReal" event=""
                             @click.native.prevent="playForReal">
                    <span>{{ $t('front.game.button.play_real') }}</span>
                </router-link>

                <router-link :to="gamePlayFunUrl" class="play-free" v-show="isPlayForFunAllowed" event=""
                             @click.native.prevent="playForFun">
                    <span>{{ $t('front.game.button.play_free') }}</span>
                </router-link>

                <router-link :to="gameInfoUrl" class="information" v-show="item.hasInfoPage">
                    <img :src="gameInfoImage" :alt="item.title">
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import {
    canPlayFun,
    gamePlayFunUrl,
    gamePlayRealUrl,
    gameInfoUrl,
    launchGameForFun,
    launchGameForReal, getGameRestrictionReasonTranslationKey
} from '../../util/game/launchGameManager';
import Toaster from "../../mixins/toaster";
import {brandName} from "../../../config/app";
import apiData from "../../mixins/apiData";

export default {
    name: "GameItem",

    mixins: [Toaster, apiData],

    props: ['item', 'imagePropertyName'],

    data: function () {
        return {
            isActive: false,
            touchEnabled: false,
            isMounted: false,
        };
    },

    mounted() {
        this.isMounted = true;

        if ('ontouchstart' in document.documentElement) {
            this.touchEnabled = true;
        }
    },

    computed: {
        gameWebpImageSrcset() {
            if (this.isMounted) {
                return (this.imagePropertyName ? this.item[this.imagePropertyName] && this.item[this.imagePropertyName].webp : this.item.coverImage && this.item.coverImage.webp) || this.itemPlaceholder;
            } else {
                return this.itemPlaceholder;
            }
        },

        gameJpgImageSrc() {
            return (this.imagePropertyName ? this.item[this.imagePropertyName] && this.item[this.imagePropertyName].jpg : this.item.coverImage && this.item.coverImage.jpg) || this.itemPlaceholder;
        },

        gameInfoImage() {
            return `/public/${brandName}/game/icon-info.svg`;
        },

        itemPlaceholder() {
            return `/public/${brandName}/game/placeholder.png`;
        },

        user() {
            return this.$store.state.user;
        },

        locale() {
            return this.$store.state.locale;
        },

        gameInfoUrl() {
            return gameInfoUrl(this.item, this.$store.getters.getLanguageUrlPrefix());
        },

        gamePlayRealUrl() {
            return gamePlayRealUrl(this.$router, this.item, this.locale);
        },

        isPlayForFunAllowed() {
            return canPlayFun(this.item, this.user);
        },

        gamePlayFunUrl() {
            return gamePlayFunUrl(this.$router, this.item, this.locale);
        },

        isGameFavorite() {
            return this.user && this.$store.state.userFavoriteGamesIds.includes(this.item.id);
        },

        restrictionReasonTranslationKey() {
            return getGameRestrictionReasonTranslationKey(this.item);
        },
    },

    methods: {
        hover() {
            setTimeout(() => this.isActive = true, 100);
        },

        leave() {
            this.isActive = false;
        },

        playForFun() {
            if (!this.isActive && this.touchEnabled) {
                return;
            }

            launchGameForFun(this, this.item);
        },

        playForReal() {
            if (!this.isActive && this.touchEnabled) {
                return;
            }

            launchGameForReal(this, this.item);
        },

        async switchFavoriteStatus() {
            if (this.isGameFavorite) {
                let {removeGameFromFavorite} = await import ("../../util/game/favoriteGame");
                void removeGameFromFavorite(this, this.item);
            } else {
                let {addGameToFavorite} = await import ("../../util/game/favoriteGame");
                void addGameToFavorite(this, this.item);
            }
        },

        showRestrictionMessage() {
            this.makeErrorToast(this.$t(this.restrictionReasonTranslationKey));
        },
    },
}
</script>

<style lang="sass">
@import "sass/components/games/GameItem"
</style>